import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-hot-toast';

export default function Login() {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  const { login } = useAuth();

  useEffect(() => {
    // Check login status on component mount
    checkLoginStatus();
  }, []);

  const checkLoginStatus = async () => {
    try {
      const response = await authAPI.getStatus();
      setLoginStatus(response.data);
    } catch (error) {
      console.error('Failed to get login status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter the password');
      return;
    }

    // Check if login is disabled before attempting
    if (loginStatus && !loginStatus.loginEnabled) {
      toast.error('Login is temporarily disabled. Please restart the server.');
      return;
    }

    setLoading(true);

    try {
      await login(password);
      toast.success('Login successful! Welcome to Hiking Safety App');
      // Navigation is handled automatically by the AuthContext
    } catch (error) {
      console.error('Login failed:', error);
      
      // Check if it's a brute force protection error
      if (error.message.includes('too many failed attempts') || 
          error.message.includes('Login disabled') ||
          error.message.includes('temporarily disabled')) {
        // toast.error('Login disabled due to too many failed attempts. Server restart required.', {
        //   duration: 8000
        // });
      } else if (error.message.includes('remainingAttempts')) {
        // Extract remaining attempts from error if available
        const match = error.message.match(/(\d+) attempt/);
        const remaining = match ? match[1] : 'few';
        toast.error(`Invalid password. ${remaining} attempts remaining before lockout.`, {
          duration: 6000
        });
      } else {
        toast.error(error.message || 'Login failed');
      }
      
      // Refresh status after failed attempt
      await checkLoginStatus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <span className="text-3xl">🏔️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Hiking Safety App
          </h1>
          <p className="text-gray-600">
            Enter your password to access the safety monitoring system
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="Enter your password"
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L9.88 9.88m-.002-.002L6.665 7.465m11.67 11.67L21.549 16.4M15.121 14.121l2.828 2.829m-2.828-2.829L15.12 14.12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || (loginStatus && !loginStatus.loginEnabled)}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing In...
              </>
            ) : loginStatus && !loginStatus.loginEnabled ? (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Login Disabled
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Login Status Indicator */}
        {loginStatus && (
          <div className="mt-4">
            {!loginStatus.loginEnabled ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-red-700 font-medium">
                    Login Disabled
                  </p>
                </div>
                <p className="text-xs text-red-600 mt-1">
                  Too many failed login attempts. Please restart the server to re-enable login.
                </p>
              </div>
            ) : loginStatus.failedAttempts > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-yellow-700 font-medium">
                    Warning: {loginStatus.remainingAttempts} attempts remaining
                  </p>
                </div>
                <p className="text-xs text-yellow-600 mt-1">
                  {loginStatus.failedAttempts} failed attempt{loginStatus.failedAttempts !== 1 ? 's' : ''}. 
                  Login will be disabled after {loginStatus.maxAttempts} failed attempts.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">
              🔐 Secure Authentication Required
            </p>
            <div className="bg-blue-50 rounded-lg p-4 text-left">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                About Hiking Safety App
              </h3>
              <p className="text-xs text-blue-700 leading-relaxed">
                This system monitors hiking trips and automatically sends emergency 
                alerts if you don't return by your scheduled time. Access is restricted 
                to authorized users only.
              </p>
            </div>
          </div>
        </div>

        {/* Demo Info */}
        {/* <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-700 text-center">
            <strong>Demo Password:</strong> hiking2026
          </p>
        </div> */}
      </div>
    </div>
  );
}